import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { apiUsers, apiSites } from '@/lib/api';
import { ROLES, ROLE_LABELS, isAdminNational, isResponsableHseSite } from '@/constants/permits';
import { useAuth } from '@/contexts/AuthContext';
import ExportImportButtons from '@/components/ExportImportButtons';
import { Save, Pencil, Trash2, X, ShieldCheck, MapPin, Users as UsersIcon, Plus, AlertOctagon } from 'lucide-react';

const ROLE_CATEGORIES = [
  { id: 'admin', label: 'Administration' },
  { id: 'travaux', label: 'Travaux' },
  { id: 'signataire', label: 'Signataires de permis' },
  { id: 'consignation', label: 'Consignation' },
];

function UserEditModal({ initial, sites, allowedSites, canSetAdmin, onSave, onClose, busy }) {
  const [form, setForm] = useState({
    name: initial?.name || '',
    roles: initial?.roles || [],
    sites: initial?.sites || [],
  });

  const toggleRole = (id) => {
    setForm((f) => ({
      ...f,
      roles: f.roles.includes(id) ? f.roles.filter((r) => r !== id) : [...f.roles, id],
    }));
  };
  const toggleSite = (code) => {
    setForm((f) => ({
      ...f,
      sites: f.sites.includes(code) ? f.sites.filter((s) => s !== code) : [...f.sites, code],
    }));
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid="user-edit-overlay">
      <div className="bg-white border-2 border-stone-900 max-w-3xl w-full mt-8 mb-8 p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-stone-500 mb-1">Édition utilisateur</div>
            <h2 className="text-2xl font-display font-black tracking-tight">{initial?.email}</h2>
          </div>
          <button onClick={onClose} className="text-stone-500 hover:text-[#DA291C]" data-testid="user-edit-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div>
          <label className="label-industrial">Nom complet</label>
          <input
            data-testid="user-edit-name"
            type="text"
            className="input-industrial w-full"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </div>

        <div className="space-y-3">
          <div className="label-industrial flex items-center gap-2">
            <ShieldCheck className="w-3 h-3" /> Rôles ({form.roles.length} sélectionné{form.roles.length > 1 ? 's' : ''})
          </div>
          {ROLE_CATEGORIES.map((cat) => {
            const catRoles = ROLES.filter((r) => r.category === cat.id);
            return (
              <div key={cat.id} className="border border-stone-200 p-3">
                <div className="text-[10px] font-bold uppercase tracking-wider text-stone-500 mb-2">{cat.label}</div>
                <div className="flex flex-wrap gap-2">
                  {catRoles.map((r) => {
                    const checked = form.roles.includes(r.id);
                    const disabled = r.id === 'admin_national' && !canSetAdmin;
                    return (
                      <button
                        key={r.id}
                        type="button"
                        data-testid={`user-edit-role-${r.id}`}
                        onClick={() => !disabled && toggleRole(r.id)}
                        disabled={disabled}
                        className={`text-xs font-bold px-3 py-1.5 border-2 transition-colors ${
                          checked
                            ? 'bg-[#09090B] text-white border-[#09090B]'
                            : 'bg-white text-stone-700 border-stone-300 hover:border-stone-600'
                        } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                        title={disabled ? "Seul l'Admin National peut attribuer ce rôle" : ''}
                      >
                        {r.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div>
          <div className="label-industrial flex items-center gap-2 mb-2">
            <MapPin className="w-3 h-3" /> Sites attribués ({form.sites.length}/{sites.length})
          </div>
          {allowedSites && allowedSites.length < sites.length && (
            <div className="text-[11px] text-amber-700 mb-2 flex items-start gap-1">
              <AlertOctagon className="w-3 h-3 mt-0.5" />
              Vous ne pouvez attribuer que les sites de votre périmètre HSE.
            </div>
          )}
          <div className="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto">
            {sites.map((s) => {
              const disabled = allowedSites && !allowedSites.includes(s.code);
              const checked = form.sites.includes(s.code);
              return (
                <button
                  key={s.code}
                  type="button"
                  data-testid={`user-edit-site-${s.code}`}
                  disabled={disabled}
                  onClick={() => toggleSite(s.code)}
                  className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 border ${
                    checked ? 'bg-[#DA291C] text-white border-[#DA291C]'
                            : 'bg-white text-stone-600 border-stone-300 hover:border-stone-500'
                  } ${disabled ? 'opacity-30 cursor-not-allowed' : ''}`}
                  title={s.name}
                >
                  {s.code}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-stone-200">
          <button onClick={onClose} disabled={busy} className="btn-industrial-secondary">Annuler</button>
          <button
            onClick={() => onSave(form)}
            disabled={busy}
            data-testid="user-edit-save"
            className="btn-industrial-primary flex items-center gap-2"
          >
            <Save className="w-4 h-4" /> Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Users() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const isAdmin = isAdminNational(currentUser);
  const isHse = isResponsableHseSite(currentUser);
  const canManage = isAdmin || isHse;

  const load = async () => {
    setLoading(true);
    try {
      const [u, s] = await Promise.all([apiUsers.list(), apiSites.list()]);
      setUsers(u.data);
      setSites(s.data);
    } catch {
      toast.error('Accès admin requis');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const canEditUser = (target) => {
    if (isAdmin) return true;
    if (!isHse) return false;
    // Miroir de la garde backend : un HSE ne peut PAS modifier un admin_national
    if ((target.roles || []).includes('admin_national')) return false;
    const myS = new Set(currentUser?.sites || []);
    const targetS = new Set(target.sites || []);
    return [...targetS].some((s) => myS.has(s)) || targetS.size === 0;
  };

  const handleSave = async (form) => {
    setBusy(true);
    try {
      await apiUsers.update(editing.user_id, form);
      toast.success('Utilisateur mis à jour');
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setBusy(false);
    }
  };
  const handleDelete = async (u) => {
    if (!window.confirm(`Supprimer définitivement le compte de ${u.name} (${u.email}) ?`)) return;
    try {
      await apiUsers.remove(u.user_id);
      toast.success('Utilisateur supprimé');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Suppression impossible');
    }
  };

  if (!canManage) {
    return <div className="text-stone-500 text-sm">Vous n'avez pas accès à cette page.</div>;
  }

  return (
    <div className="max-w-7xl space-y-6" data-testid="users-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-stone-500 mb-2">Administration</div>
          <h1 className="text-4xl font-display font-black tracking-tighter">Utilisateurs</h1>
          <p className="text-stone-600 mt-2 text-sm">
            {isAdmin ? `${users.length} utilisateurs sur la plateforme` : `${users.length} utilisateurs de vos sites`}
          </p>
        </div>
        <div className="inline-flex items-center gap-3">
          <ExportImportButtons
            exportPath="/admin/export/users.csv"
            importPath="/admin/import/users"
            filenameHint="utilisateurs_lafarge.csv"
            visible={isAdmin}
            onImported={() => load()}
          />
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-stone-600 bg-stone-100 px-3 py-1.5">
            <UsersIcon className="w-4 h-4" />
            {isAdmin ? 'Admin National' : 'Responsable HSE Site'}
          </div>
        </div>
      </div>

      {loading ? <div className="text-stone-500">Chargement...</div> : (
        <div className="section-card p-0 overflow-hidden">
          <table className="w-full">
            <thead className="bg-stone-50 border-b-2 border-stone-200">
              <tr>
                <th className="text-left text-[10px] uppercase font-bold tracking-wider text-stone-600 px-6 py-3">Utilisateur</th>
                <th className="text-left text-[10px] uppercase font-bold tracking-wider text-stone-600 px-6 py-3">Rôles</th>
                <th className="text-left text-[10px] uppercase font-bold tracking-wider text-stone-600 px-6 py-3">Sites</th>
                <th className="text-right text-[10px] uppercase font-bold tracking-wider text-stone-600 px-6 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const editable = canEditUser(u);
                return (
                  <tr key={u.user_id} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`user-row-${u.user_id}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {u.picture ? <img src={u.picture} className="w-9 h-9 rounded-full" alt="" /> : (
                          <div className="w-9 h-9 rounded-full bg-stone-300 flex items-center justify-center text-xs font-bold text-stone-700">
                            {(u.name || '?')[0]?.toUpperCase()}
                          </div>
                        )}
                        <div>
                          <div className="font-semibold text-sm text-[#09090B]">{u.name}</div>
                          <div className="text-xs text-stone-500">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1 max-w-md">
                        {(u.roles || []).length === 0 && <span className="text-[11px] italic text-stone-400">Aucun</span>}
                        {(u.roles || []).map((r) => (
                          <span
                            key={r}
                            className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 border ${
                              r === 'admin_national' ? 'bg-[#DA291C] text-white border-[#DA291C]'
                              : r === 'responsable_hse_site' ? 'bg-[#09090B] text-white border-[#09090B]'
                              : 'bg-stone-100 text-stone-700 border-stone-300'
                            }`}
                          >
                            {ROLE_LABELS[r] || r}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1 max-w-xs">
                        {(u.sites || []).length === 0 ? <span className="text-[11px] italic text-stone-400">Aucun</span> :
                          (u.sites || []).slice(0, 6).map((s) => (
                            <span key={s} className="text-[9px] font-mono font-bold bg-stone-100 px-1.5 py-0.5 border border-stone-300">{s}</span>
                          ))}
                        {(u.sites || []).length > 6 && (
                          <span className="text-[9px] font-bold text-stone-500">+{u.sites.length - 6}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      {editable && currentUser?.user_id !== u.user_id ? (
                        <div className="inline-flex items-center gap-2">
                          <button
                            data-testid={`user-edit-${u.user_id}`}
                            onClick={() => setEditing(u)}
                            className="text-xs uppercase font-bold tracking-wider text-stone-700 hover:text-[#09090B] inline-flex items-center gap-1"
                          >
                            <Pencil className="w-3 h-3" /> Modifier
                          </button>
                          <button
                            data-testid={`user-delete-${u.user_id}`}
                            onClick={() => handleDelete(u)}
                            className="text-xs uppercase font-bold tracking-wider text-[#DA291C] hover:text-red-700 inline-flex items-center gap-1"
                          >
                            <Trash2 className="w-3 h-3" /> Supprimer
                          </button>
                        </div>
                      ) : (
                        <span className="text-[10px] italic text-stone-400">
                          {currentUser?.user_id === u.user_id ? 'Vous' : 'Hors périmètre'}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <UserEditModal
          initial={editing}
          sites={sites}
          allowedSites={isAdmin ? null : currentUser?.sites || []}
          canSetAdmin={isAdmin}
          busy={busy}
          onSave={handleSave}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
